"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useInvoiceStore } from "@/lib/invoice/store";
import { addDays, computeTotals, daysBetween, fmtDate, fmtMoney, invoiceStatus, todayISO } from "@/lib/invoice/utils";
import { presetById } from "@/lib/invoice/presets";
import IndustryPicker from "@/components/Invoice/IndustryPicker";
import { Button, Card, Empty, Input, Loading, PageHeader, Stat, StatusBadge, cx, useFeedback } from "@/components/Invoice/ui";

const FILTERS = [
  ["", "All"],
  ["open", "Open"],
  ["accepted", "Accepted"],
  ["declined", "Declined"],
  ["expired", "Expired"],
  ["draft", "Draft"],
];

export default function QuotationsPage() {
  const store = useInvoiceStore();
  const router = useRouter();
  const { toast } = useFeedback();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [allIndustries, setAllIndustries] = useState(false);

  const rows = useMemo(
    () =>
      store.invoices
        .filter((i) => i.docType === "quote")
        .map((inv) => {
          const t = computeTotals(inv);
          const status = invoiceStatus(inv, t);
          const expired = status === "sent" && inv.dueDate && inv.dueDate < todayISO();
          const converted = store.invoices.some((x) => x.fromQuoteId === inv.id && x.status !== "cancelled");
          return { inv, t, status, expired, converted };
        })
        .sort((a, b) => String(b.inv.date).localeCompare(String(a.inv.date)) || String(b.inv.number).localeCompare(String(a.inv.number))),
    [store.invoices]
  );

  if (!store.ready) return <Loading />;

  const shown = rows.filter((r) => {
    if (filter === "open" && (r.status !== "sent" || r.expired)) return false;
    if (filter === "expired" && !r.expired) return false;
    if (["accepted", "declined", "draft"].includes(filter) && r.status !== filter) return false;
    return [r.inv.number, r.inv.billTo?.name, r.inv.subject, ...(r.inv.items || []).map((l) => l.description)].join(" ").toLowerCase().includes(q.toLowerCase());
  });

  const sum = (list) => {
    const byCur = {};
    for (const r of list) byCur[r.inv.currency] = (byCur[r.inv.currency] || 0) + r.t.total;
    return Object.entries(byCur).map(([c, v]) => fmtMoney(v, c)).join(" + ") || "—";
  };
  const open = rows.filter((r) => r.status === "sent" && !r.expired);
  // business types of the profiles in use – their templates come first
  const industries = [...new Set(store.brands.map((b) => b.industry).filter((x) => presetById(x)))];
  const showIds = allIndustries || !industries.length ? null : [...industries, ...(industries.includes("general") ? [] : ["general"])];
  const soon = addDays(todayISO(), 3);
  const accepted = rows.filter((r) => r.status === "accepted");
  const decided = rows.filter((r) => ["accepted", "declined"].includes(r.status)).length;

  const mark = (inv, status) => {
    store.saveInvoice({ ...inv, status }, { action: status === "accepted" ? "Quote accepted" : "Quote declined" });
    toast(status === "accepted" ? "Marked accepted" : "Marked declined", { tone: "success" });
  };

  return (
    <div>
      <PageHeader
        eyebrow="Sales"
        title="Quotations"
        subtitle="Send professional quotes from ready-made industry templates, track which are accepted and turn them into invoices in one click."
        actions={
          <Link href="/invoice/invoices/edit?type=quote">
            <Button>+ New quotation</Button>
          </Link>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Open quotes" value={open.length} sub={sum(open)} />
        <Stat label="Accepted" value={accepted.length} sub={sum(accepted)} />
        <Stat label="Win rate" value={decided ? `${Math.round((accepted.length / decided) * 100)}%` : "—"} sub={`${decided} decided`} />
        <Stat label="Expired" value={rows.filter((r) => r.expired).length} sub="past their validity date" />
      </div>

      <Card
        title={industries.length ? `Quotation templates for ${industries.map((i) => presetById(i).label).join(" & ")}` : "Start a quotation from an industry template"}
        subtitle={
          industries.length
            ? "Scope lines, options and terms that fit your business – just add prices"
            : "Tip: set your business type in Settings → Business profiles to see only the templates and options that fit your business"
        }
        actions={
          industries.length ? (
            <Button size="sm" variant="ghost" onClick={() => setAllIndustries((v) => !v)}>
              {allIndustries ? "Only my business" : "Show all industries"}
            </Button>
          ) : (
            <Link href="/invoice/settings?tab=brands">
              <Button size="sm" variant="secondary">Set business type</Button>
            </Link>
          )
        }
        className="mb-6"
      >
        <IndustryPicker ids={showIds} onPick={(p) => router.push(`/invoice/invoices/edit?type=quote&preset=${p.id}`)} />
      </Card>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input className="max-w-xs" placeholder="Search number, client, item…" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(([k, l]) => (
            <button
              key={k || "all"}
              onClick={() => setFilter(k)}
              className={cx("rounded-full border px-3 py-1 text-xs font-medium", filter === k ? "border-brand-700 bg-brand-700 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300")}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <Empty title={rows.length ? "No quotations match" : "No quotations yet"}>
          {rows.length ? "Try another filter." : "Pick an industry template above, or start a blank quotation."}
        </Empty>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200/80 bg-white shadow-card">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Quotation</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Valid until</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map(({ inv, t, status, expired, converted }) => (
                <tr key={inv.id} className="hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <Link href={`/invoice/invoices/view?id=${inv.id}`} className="font-semibold text-slate-900 hover:text-brand-700">
                      {inv.number}
                    </Link>
                    {inv.subject && <div className="max-w-[260px] truncate text-[11px] text-slate-500">{inv.subject}</div>}
                  </td>
                  <td className="px-4 py-3">{inv.billTo?.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(inv.date, "pretty")}</td>
                  <td className={cx("px-4 py-3", expired ? "font-medium text-amber-700" : "text-slate-600")}>
                    {inv.dueDate ? fmtDate(inv.dueDate, "pretty") : "—"}
                    {expired && <div className="text-[11px]">Expired</div>}
                    {!expired && status === "sent" && inv.dueDate && inv.dueDate <= soon && <div className="text-[11px] font-medium text-amber-700">Expires soon</div>}
                    {status === "sent" && inv.lastFollowUpAt && <div className="text-[11px] text-slate-400">Followed up {daysBetween(inv.lastFollowUpAt, todayISO())}d ago</div>}
                  </td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums">{fmtMoney(t.total, inv.currency)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={status} />
                    {converted && <div className="mt-0.5 text-[11px] text-emerald-700">Invoiced</div>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <div className="flex justify-end gap-1.5">
                      {status === "sent" && (
                        <>
                          <Button size="sm" variant="secondary" onClick={() => router.push(`/invoice/invoices/view?id=${inv.id}&compose=followup`)} title="WhatsApp / email follow-up with an approval link">
                            Follow up
                          </Button>
                          <Button size="sm" variant="success" onClick={() => mark(inv, "accepted")}>Accept</Button>
                          <Button size="sm" variant="ghost" onClick={() => mark(inv, "declined")}>Decline</Button>
                        </>
                      )}
                      {status !== "declined" && !converted && (
                        <Button size="sm" variant="secondary" onClick={() => router.push(`/invoice/invoices/edit?duplicate=${inv.id}&type=invoice`)}>
                          Convert to invoice
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => router.push(`/invoice/invoices/edit?duplicate=${inv.id}`)}>
                        Duplicate
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
